import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ErrorHandler,
  EventEmitter,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core'
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { disabled, FieldTree, form, required } from '@angular/forms/signals'

import { MatSnackBar } from '@angular/material/snack-bar'

import { EmailLahetysStatus, EmailLahetysStatusKoodi } from '@lemon/core-firebase/model/lasku'
import { RaporttiType } from '@lemon/core-firebase/model/reports-shared'
import { LocalDate, LocalMonth, NumberDate, TuettuKieli } from '@lemon/core/model/common'
import { AlvIlmoitusjakso, Asiakas, KirjanpitoraporttienLahetystiedot } from '../_jaettu-lemonator/model/asiakas'
import { KirjanpitajanNimitiedot } from '../_jaettu-lemonator/model/kirjanpitaja'
import {
  AlvIlmoitus,
  EmailLiite,
  EmailLiiteDownloadRequest,
  EmailLiiteDownloadResponse,
  KirjanpitoEmailLahetys,
  KirjanpitoEmailLahetysHistoryPdfRequest,
  KirjanpitoEmailLahetysHistoryPdfResponse,
  KirjanpitoEmailLahetysTyojono,
  KirjanpitoEmailTextAutosaveData,
  Raportointikirjaus,
  RaporttiName,
  RaporttiPdfResponse,
  RaporttiRequest,
} from '../_jaettu-lemonator/model/kirjanpito'

import { Yritysmuoto } from '@lemon/core-firebase/model/kayttaja'
import { TranslationService } from '@lemon/core-firebase/service/translation.service'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'
import { AsiakasService, AsiakkaanAvainTiedot } from '../_angular/service/asiakas/asiakas.service'
import { KirjanpitajaService } from '../_angular/service/kirjanpitaja/kirjanpitaja.service'
import { KirjanpitajanTiedot, KirjautunutKayttajaService } from '../_angular/service/kirjautunut-kayttaja.service'
import { arvonMuutokset, asetaArvo } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from '../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../_jaettu-angular/service/ladataan.service'
import { TimestampService } from '../_jaettu-angular/service/timestamp-service'
import { AsiakasJaettuService } from '../_jaettu-lemonator/service/asiakas-jaettu.service'
import { KirjanpitoJaettuService } from '../_jaettu-lemonator/service/kirjanpito-jaettu.service'
import { KirjanpitoUriService } from '../_jaettu-lemonator/service/kirjanpito-uri.service'

import { BehaviorSubject, Observable, Subject, combineLatest, defer, firstValueFrom, of as observableOf } from 'rxjs'
import { delay, distinctUntilChanged, filter, map, startWith, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators'

import { ApixReceivedInvoiceConfig } from '@lemon/core-firebase/model/apix'
import { DebugService } from 'app/_angular/service/debug.service'
import { FirebaseLemonaid, FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'
import { FileSystemFileEntry } from 'app/_jaettu-angular/file-drop/file-drop-dom.types'
import { FileDropEntry } from 'app/_jaettu-angular/file-drop/file-drop-entry'
import { FileSaverService } from 'app/_jaettu-angular/service/file-saver'
import { TiedostojenLataamisService } from 'app/_jaettu-angular/service/tiedostojen-lataamis.service'

interface HistoriaEmailLiite extends EmailLiite {
  firestoreLinkki?: string
  firestoreLinkkiEncoded?: string
}

interface LahetysHistoria extends KirjanpitoEmailLahetys {
  recipientsEmails: string
  liitteet: HistoriaEmailLiite[]
  nimi: string
  luotuDateStr: string
  firestoreLinkki?: string
  firestoreLinkkiEncoded?: string
}

interface LahetysLomake {
  aihe: string
  tekstinAlku: string
  tekstinLoppu: string
  tuloslaskelma: boolean
  tase: boolean
  paakirja: boolean
  alvIlmoitus: boolean
  selvitettavatOstot: boolean
  selvitettavatMyynnit: boolean
  lahettaja: string
  vastaanottajat: string
  piilokopioLahettajalle: boolean
}

@Component({
  selector: '[app-kirjanpito-lahetys]',
  templateUrl: './kirjanpito-lahetys.component.html',
  styleUrls: ['./kirjanpito-lahetys.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class KirjanpitoLahetysComponent implements OnInit, OnDestroy {
  @Input() valittuKuukausiObservable: Observable<LocalMonth>
  @Input() latestAlvIlmoitusObservable: Observable<AlvIlmoitus>
  @Input() naytaAlvIlmoitusValilehtiObservable: Observable<boolean>

  @Output() onkoSpostejaLahetetty: EventEmitter<boolean> = new EventEmitter()

  // @ViewChild('tekstiTextareaLoppu', { read: MatInput, static: true }) tekstiTextareaLoppu: MatInput

  private readonly _injector = inject(Injector)
  private readonly _piilokopioPoissa = signal(false)
  private readonly _alvIlmoitusPoissa = signal(false)
  private readonly _selvitettavatOstotPoissa = signal(false)
  private readonly _selvitettavatMyynnitPoissa = signal(false)

  lomake = form(
    signal<LahetysLomake>({
      aihe: '',
      tekstinAlku: '',
      tekstinLoppu: '',
      tuloslaskelma: false,
      tase: false,
      paakirja: false,
      alvIlmoitus: false,
      selvitettavatOstot: false,
      selvitettavatMyynnit: false,
      lahettaja: '',
      vastaanottajat: '',
      piilokopioLahettajalle: false,
    }),
    polku => {
      required(polku.aihe)
      required(polku.tekstinAlku)
      required(polku.tekstinLoppu)
      disabled(polku.alvIlmoitus, () => this._alvIlmoitusPoissa())
      disabled(polku.selvitettavatOstot, () => this._selvitettavatOstotPoissa())
      disabled(polku.selvitettavatMyynnit, () => this._selvitettavatMyynnitPoissa())
      disabled(polku.lahettaja)
      disabled(polku.vastaanottajat)
      disabled(polku.piilokopioLahettajalle, () => this._piilokopioPoissa())
    },
  )

  // Reaktiivisen lomakkeen value jätti käytöstä poistetut kentät pois
  readonly lahetyksenArvot = computed(() => {
    const arvot: Partial<LahetysLomake> = { ...this.lomake().value() }
    for (const avain of Object.keys(arvot) as (keyof LahetysLomake)[]) {
      if (this.lomake[avain]().disabled()) {
        delete arvot[avain]
      }
    }
    return arvot
  })
  sentThisMonth: LahetysHistoria[]
  // erapaivaObservable: Observable<LocalDate>
  nykyisenKirjanpitajanNimitiedotObservable: Observable<KirjanpitajanNimitiedot>
  attachmentsTotalSizeIfTooBigObservable: Observable<number>
  clientTypeObservable: Observable<'holvi' | 'regular' | 'unknown'>
  emailLiitteetObservable: Observable<EmailLiite[]>
  vastaanottajat: string[] = []
  historyLoading: boolean = true
  latausVirhe: string = null

  private _sendingInProgress: boolean
  private _asiakasObservable: Observable<Asiakas>
  private _lahetysAvainSubject: BehaviorSubject<string> = new BehaviorSubject(null)

  private _ngUnsubscribe = new Subject<void>()

  includeMainAutotext: boolean = true
  includeSelvitettavatAutotext: boolean = true
  mainAutotext: string
  selvitettavatAutotext: string
  autosaveTimeTextObservable: Observable<string>
  asiakasAvain: string
  asiakkaanKieliObservable: Observable<TuettuKieli>

  constructor(
    private _asiakasService: AsiakasService,
    private _asiakasJaettuService: AsiakasJaettuService,
    private _translationService: TranslationService,
    private _dateService: DateService,
    private _firebaseLemonator: FirebaseLemonator,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _timestampService: TimestampService,
    private _ladataanService: LadataanService,
    private _kayttajaService: KirjautunutKayttajaService,
    private _errorHandler: ErrorHandler,
    private _snackbar: MatSnackBar,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _currencyService: CurrencyService,
    private _kirjanpitoJaettuService: KirjanpitoJaettuService,
    private _formValidationService: FormValidationService,
    private _tiedostojenLataamisService: TiedostojenLataamisService,
    private _fileSaverService: FileSaverService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _debugService: DebugService,
  ) {
    this.nykyisenKirjanpitajanNimitiedotObservable = combineLatest([
      this._asiakasService.nykyinenAsiakasObservable,
      this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable,
      this._kayttajaService.kirjanpitajanTiedotObservable,
    ]).pipe(
      map(([asiakas, kirjanpitajatMap, kirjanpitaja]) => {
        this.asiakasAvain = asiakas?.avain || null
        if (!kirjanpitajatMap || !kirjanpitaja) {
          return null
        }
        if (asiakas.kasittelija === 'QgPvtcCjoOdf6Zg7lgMwqLWp2BG2') {
          // Holvi-asiakas
          return kirjanpitajatMap.get('QgPvtcCjoOdf6Zg7lgMwqLWp2BG2')
        }
        return kirjanpitajatMap.get(kirjanpitaja.uid)
      }),
    )
  }

  public focus() {
    // setTimeout(() => {
    //   this.tekstiTextareaLoppu.focus()
    // }, 50)
  }

  ngOnInit() {
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector })

    this._asiakasObservable = this._asiakasService.nykyinenAsiakasObservable.pipe(
      distinctUntilChanged((a, b) => {
        return a?.avain === b?.avain
      }),
    )

    this.asiakkaanKieliObservable = this._asiakasObservable.pipe(map(asiakas => asiakas.laskunKieli || 'fi'))

    const tunnistetutYhteyshenkilot = this._asiakasService.nykyisenAsiakkaanKayttajatObservable.pipe(
      map(kayttajat => {
        if (!kayttajat?.length) {
          return []
        }
        return kayttajat.filter(k => k.aktiivinen && k.roolit.HALLINTO_YHTEYSHENKILO && k.kayttajaTunnistettu === 'tunnistettu-nets')
      }),
    )

    combineLatest([tunnistetutYhteyshenkilot, this._asiakasObservable])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(([yhteyshenkilot, asiakas]) => {
        this.vastaanottajat = yhteyshenkilot.map(yh => yh.email)
        if (asiakas.kirjanpitoviestienLisavastaanottajat) {
          this.vastaanottajat.push(...asiakas.kirjanpitoviestienLisavastaanottajat)
        }
        asetaArvo(this.lomake.vastaanottajat, this.vastaanottajat.join(', '))
      })

    muutokset(this.lomake.piilokopioLahettajalle)
      .pipe(distinctUntilChanged(), withLatestFrom(this.valittuKuukausiObservable, this._asiakasObservable), takeUntil(this._ngUnsubscribe))
      .subscribe(async ([valittu, kuukausi, asiakas]) => {
        this._updateAutosaveData({ piilokopioValittu: !!valittu }, kuukausi, asiakas)
      })
    muutokset(this.lomake.paakirja)
      .pipe(distinctUntilChanged(), withLatestFrom(this.valittuKuukausiObservable, this._asiakasObservable), takeUntil(this._ngUnsubscribe))
      .subscribe(async ([valittu, kuukausi, asiakas]) => {
        this._updateAutosaveData({ paakirjaValittu: !!valittu }, kuukausi, asiakas)
      })
    muutokset(this.lomake.tase)
      .pipe(distinctUntilChanged(), withLatestFrom(this.valittuKuukausiObservable, this._asiakasObservable), takeUntil(this._ngUnsubscribe))
      .subscribe(async ([valittu, kuukausi, asiakas]) => {
        this._updateAutosaveData({ taseValittu: !!valittu }, kuukausi, asiakas)
      })
    muutokset(this.lomake.tuloslaskelma)
      .pipe(distinctUntilChanged(), withLatestFrom(this.valittuKuukausiObservable, this._asiakasObservable), takeUntil(this._ngUnsubscribe))
      .subscribe(async ([valittu, kuukausi, asiakas]) => {
        this._updateAutosaveData({ tulosValittu: !!valittu }, kuukausi, asiakas)
      })
    muutokset(this.lomake.alvIlmoitus)
      .pipe(distinctUntilChanged(), withLatestFrom(this.valittuKuukausiObservable, this._asiakasObservable), takeUntil(this._ngUnsubscribe))
      .subscribe(async ([valittu, kuukausi, asiakas]) => {
        this._updateAutosaveData({ alvValittu: !!valittu }, kuukausi, asiakas)
      })
    muutokset(this.lomake.selvitettavatOstot)
      .pipe(
        distinctUntilChanged(),
        delay(2000), // Delay to not override first load of KirjanpitoEmailTextAutosaveData
        withLatestFrom(this.valittuKuukausiObservable, this._asiakasObservable),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(async ([valittu, kuukausi, asiakas]) => {
        this._updateAutosaveData({ selvOstotValittu: !!valittu }, kuukausi, asiakas)
      })
    muutokset(this.lomake.selvitettavatMyynnit)
      .pipe(
        distinctUntilChanged(),
        delay(2000), // Delay to not override first load of KirjanpitoEmailTextAutosaveData
        withLatestFrom(this.valittuKuukausiObservable, this._asiakasObservable),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(async ([valittu, kuukausi, asiakas]) => {
        this._updateAutosaveData({ selvMyynnitValittu: !!valittu }, kuukausi, asiakas)
      })

    this.clientTypeObservable = this._asiakasObservable.pipe(
      map(asiakas => {
        if (!asiakas) {
          return 'unknown'
        }
        return this._kirjanpitoJaettuService.getClientTypeAsString(asiakas)
      }),
    )

    const senderEmailObservable: Observable<string> = this._asiakasObservable.pipe(
      switchMap(asiakas => {
        if (!asiakas) {
          return observableOf<string>(null)
        }
        const tyyppi = this._kirjanpitoJaettuService.getClientTypeAsString(asiakas)
        if (tyyppi === 'holvi') {
          return observableOf<string>('no-reply@lemontree.fi')
        }
        return this._kirjanpitajaService.kirjautuneenKayttajanKirjanpitajaObservable.pipe(map(kirjanpitaja => kirjanpitaja?.email || null))
      }),
    )

    senderEmailObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(email => {
      asetaArvo(this.lomake.lahettaja, email ?? '')
    })

    const annaSelvitettavienAikajakso: Observable<{ alku: NumberDate; loppu: NumberDate }> = this._asiakasObservable.pipe(
      map(asiakas => {
        if (!asiakas?.tilikaudet?.length) {
          return null
        }
        return this._asiakasJaettuService.annaAikajaksoSelvitettavatRaporttiaVarten(asiakas.tilikaudet)
      }),
      distinctUntilChanged((prev, current) => {
        if (prev && !current) {
          return false
        } else if (!prev && current) {
          return false
        } else if (!prev && !current) {
          return true
        }
        if (prev.alku !== current.alku || prev.loppu !== current.loppu) {
          return false
        }
        return true
      }),
    )

    const selvitettavatOstotRaakadataObservable: Observable<Raportointikirjaus[]> = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, annaSelvitettavienAikajakso]).pipe(
      switchMap(([asiakas, aikajakso]) => {
        if (!asiakas || !aikajakso) {
          return observableOf(null)
        }
        const collectionUri = this._kirjanpitoUriService.annaRaporttikirjausCollectionUri(asiakas.avain)
        return this._firebaseLemonator
          .firestoreCollection<Raportointikirjaus>(collectionUri)
          .where('a', 'array-contains-any', ['1777'])
          .where('p', '<', aikajakso.loppu + 1)
          .where('p', '>', aikajakso.alku - 1)
          .orderBy('p', 'desc')
          .limit(1)
          .listen()
      }),
    )

    const selvitettavatMyynnitRaakadataObservable: Observable<Raportointikirjaus[]> = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, annaSelvitettavienAikajakso]).pipe(
      switchMap(([asiakas, aikajakso]) => {
        if (!asiakas || !aikajakso) {
          return observableOf(null)
        }
        const collectionUri = this._kirjanpitoUriService.annaRaporttikirjausCollectionUri(asiakas.avain)
        return this._firebaseLemonator
          .firestoreCollection<Raportointikirjaus>(collectionUri)
          .where('a', 'array-contains-any', ['3099'])
          .where('p', '<', aikajakso.loppu + 1)
          .where('p', '>', aikajakso.alku - 1)
          .orderBy('p', 'desc')
          .limit(1)
          .listen()
      }),
    )

    selvitettavatOstotRaakadataObservable
      .pipe(
        filter(data => !!data),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(selvitettavatOstotRawData => {
        if (selvitettavatOstotRawData?.length > 0) {
          this._asetaKaytostaPois(this.lomake.selvitettavatOstot, this._selvitettavatOstotPoissa, false)
          asetaArvo(this.lomake.selvitettavatOstot, true)
        } else {
          asetaArvo(this.lomake.selvitettavatOstot, false)
          this._asetaKaytostaPois(this.lomake.selvitettavatOstot, this._selvitettavatOstotPoissa, true)
        }
      })

    selvitettavatMyynnitRaakadataObservable
      .pipe(
        filter(data => !!data),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(selvitettavatMyynnitRawData => {
        if (selvitettavatMyynnitRawData?.length > 0) {
          this._asetaKaytostaPois(this.lomake.selvitettavatMyynnit, this._selvitettavatMyynnitPoissa, false)
          asetaArvo(this.lomake.selvitettavatMyynnit, true)
        } else {
          asetaArvo(this.lomake.selvitettavatMyynnit, false)
          this._asetaKaytostaPois(this.lomake.selvitettavatMyynnit, this._selvitettavatMyynnitPoissa, true)
        }
      })

    const raportitFormsObservable: Observable<string[]> = combineLatest([
      this._asiakasObservable,
      this._annaArvoJaMuutokset(this.lomake.tuloslaskelma),
      this._annaArvoJaMuutokset(this.lomake.tase),
      this._annaArvoJaMuutokset(this.lomake.paakirja),
      this._annaArvoJaMuutokset(this.lomake.alvIlmoitus),
    ]).pipe(
      map(([asiakas, tuloslaskelma, tase, paakirja, alv]) => {
        const kieli = asiakas.laskunKieli || 'fi'
        const namesOfSelected: string[] = []
        if (tuloslaskelma) {
          namesOfSelected.push(this._translationService.lokalisoi('kirjanpito-lahetys.tuloslaskelma', kieli))
        }
        if (tase) {
          namesOfSelected.push(this._translationService.lokalisoi('kirjanpito-lahetys.tase', kieli))
        }
        if (paakirja) {
          namesOfSelected.push(this._translationService.lokalisoi('kirjanpito-lahetys.paakirja', kieli))
        }
        if (alv) {
          namesOfSelected.push(this._translationService.lokalisoi('kirjanpito-lahetys.alv-ilmoitus', kieli))
        }
        return namesOfSelected
      }),
      distinctUntilChanged((a, b) => {
        return JSON.stringify(a) === JSON.stringify(b)
      }),
    )

    this.emailLiitteetObservable = this._lahetysAvainSubject.asObservable().pipe(
      withLatestFrom(this._asiakasObservable),
      switchMap(([lahetysAvain, asiakas]) => {
        if (!asiakas) {
          return observableOf<EmailLiite[]>([])
        }
        const uri = this._kirjanpitoUriService.annaKirjanpitoEmailLahetyksenLiitteetCollection(asiakas.avain, lahetysAvain)
        return this._firebaseLemonator.firestoreCollection<EmailLiite>(uri).where('poistettu', '==', false).listen()
      }),
      takeUntil(this._ngUnsubscribe),
    )

    this.attachmentsTotalSizeIfTooBigObservable = combineLatest([
      this._annaArvoJaMuutokset(this.lomake.tuloslaskelma),
      this._annaArvoJaMuutokset(this.lomake.tase),
      this._annaArvoJaMuutokset(this.lomake.paakirja),
      this._annaArvoJaMuutokset(this.lomake.alvIlmoitus),
      this.emailLiitteetObservable.pipe(startWith([] as EmailLiite[])),
    ]).pipe(
      map(([tuloslaskelma, tase, paakirja, alv, liitteet]) => {
        let totalKoko = 0
        const averageReportSize = 100 * 1000 // 100 KB
        if (tuloslaskelma) {
          totalKoko += averageReportSize
        }
        if (tase) {
          totalKoko += averageReportSize
        }
        if (paakirja) {
          totalKoko += averageReportSize
        }
        if (alv) {
          totalKoko += averageReportSize
        }

        for (const liite of liitteet || []) {
          totalKoko += liite.koko || 512 * 1000 // 512 KB
        }

        if (totalKoko > Math.pow(10, 7)) {
          return this._currencyService.roundHalfUp(totalKoko / Math.pow(10, 6), 1)
        }
        return 0
      }),
      takeUntil(this._ngUnsubscribe),
    )

    // this.erapaivaObservable = combineLatest([this.valittuKuukausiObservable, this._asiakasObservable]).pipe(
    //   map(([kuukausi, asiakas]) => {
    //     if (!kuukausi || !asiakas) {
    //       return null
    //     }
    //     const kuunEnsimmainen: LocalDate = { year: kuukausi.year, month: kuukausi.month, day: 1 }
    //     const nykyinenIlmoitusjakso = this._asiakasJaettuService.annaNykyinenAlvIlmoitusjaksoPaivalle(asiakas, this._dateService.localDateToDate(kuunEnsimmainen))
    //     const seuraavaIlmoitusjakso = this._asiakasJaettuService.annaSeuraavaAlvIlmoitusjakso(asiakas, nykyinenIlmoitusjakso)
    //     const clientType: 'holvi' | 'regular' = this._kirjanpitoJaettuService.getClientTypeAsString(asiakas)
    //     return this._kirjanpitoJaettuService.annaAlvnErapaiva(clientType, kuukausi, nykyinenIlmoitusjakso, seuraavaIlmoitusjakso)
    //   }),
    //   distinctUntilChanged((a, b) => {
    //     return this._dateService.localDateToNumber(a) === this._dateService.localDateToNumber(b)
    //   })
    // )

    const rawSavedTextsInDatabaseObservable: Observable<Partial<KirjanpitoEmailTextAutosaveData>> = combineLatest([this.valittuKuukausiObservable, this._asiakasObservable]).pipe(
      switchMap(([kuukausi, asiakas]) => {
        asetaArvo(this.lomake.aihe, '')
        asetaArvo(this.lomake.tekstinAlku, '')
        asetaArvo(this.lomake.tekstinLoppu, '')

        if (!kuukausi || !asiakas) {
          return observableOf<KirjanpitoEmailTextAutosaveData>(null)
        }

        const kieli = asiakas.laskunKieli || 'fi'
        const autosaveDoc = this._firebaseLemonator.firestoreDoc<KirjanpitoEmailTextAutosaveData>(this._kirjanpitoUriService.annaKirjanpitoEmailTekstiAutosaveUri(asiakas.avain, kuukausi))
        return autosaveDoc.listen().pipe(
          withLatestFrom(this._kayttajaService.kirjanpitajanTiedotObservable, this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable),
          map(([autosaved, kirjanpitajanTiedot, nimitiedotMap]) => {
            const clientType: 'holvi' | 'regular' = this._kirjanpitoJaettuService.getClientTypeAsString(asiakas)
            const newText: Partial<KirjanpitoEmailTextAutosaveData> = {
              aihe: this._annaAihe(autosaved, kuukausi, asiakas, kieli),
              tekstinAlku: this._getTextStartFromAutosaveData(autosaved, kieli, clientType),
              tekstinLoppu: this._getTextEndFromAutosaveData(autosaved, kieli, clientType, kirjanpitajanTiedot, nimitiedotMap),
              paivittaja: autosaved?.paivittaja || null,
              paivitettu: autosaved?.paivitettu || null,
            }
            return newText
          }),
        )
      }),
      lemonShare(),
    )

    this.autosaveTimeTextObservable = combineLatest([rawSavedTextsInDatabaseObservable, this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable]).pipe(
      map(([dbTextDocument, nimitiedotMap]) => {
        if (dbTextDocument?.paivitettu) {
          if (nimitiedotMap) {
            const kirjanpitaja = nimitiedotMap.get(dbTextDocument.paivittaja)
            if (kirjanpitaja) {
              return 'Tallentanut ' + kirjanpitaja.etunimi + ' ' + kirjanpitaja.sukunimi + ' ' + this._dateService.muotoilePaivaJaAikaDate(dbTextDocument.paivitettu.toDate(), 'fi')
            }
          }
          return 'Tallennettu ' + this._dateService.muotoilePaivaJaAikaDate(dbTextDocument.paivitettu.toDate(), 'fi')
        }
        return ''
      }),
    )

    rawSavedTextsInDatabaseObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(texts => {
      if (!this.lomake.aihe().value()?.trim()) {
        asetaArvo(this.lomake.aihe, texts.aihe ?? '')
      }
      if (!this.lomake.tekstinAlku().value()) {
        // Note: Don't include trim() to allow emptying text boxes
        asetaArvo(this.lomake.tekstinAlku, texts.tekstinAlku ?? '')
      }
      if (!this.lomake.tekstinLoppu().value()) {
        // Note: Don't include trim() to allow emptying text boxes
        asetaArvo(this.lomake.tekstinLoppu, texts.tekstinLoppu ?? '')
      }
    })

    const nonVatAutotextObservable = combineLatest([raportitFormsObservable, this.valittuKuukausiObservable, this._asiakasObservable]).pipe(
      map(([selectedLiitteet, kuukausi, asiakas]) => {
        if (!kuukausi || !asiakas) {
          return null
        }

        const kieli = asiakas.laskunKieli || 'fi'
        const clientType: 'holvi' | 'regular' = this._kirjanpitoJaettuService.getClientTypeAsString(asiakas)
        const kuukaudenNimi = this._dateService.annaKuukaudenNimiPaikallinen(kuukausi, kieli)

        // Format liitteet list (final two should have ' ja ' between them)
        const liitteetFormatted: string = this._createLiitteetListText(selectedLiitteet, kieli, clientType)

        // Text for non-VAT clients
        const nonVatMailText = 'kirjanpito-lahetys.' + clientType + '.mail-text-non-alv-client'
        // Return text for non-VAT client
        const nonVatClientTeksti = this._translationService.lokalisoi(nonVatMailText, kieli, {
          kuukaudenNimi: kuukaudenNimi,
          liitteetText: liitteetFormatted,
        })
        return nonVatClientTeksti
      }),
    )

    const apixSettingsObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(asiakas => {
        if (asiakas?.avain) {
          return this._firebaseLemonaid.firestoreDoc<ApixReceivedInvoiceConfig>('customers/' + asiakas.avain + '/apix-received-invoice-config/' + asiakas.avain).listen()
        }
        return observableOf<ApixReceivedInvoiceConfig>(null)
      }),
    )

    const alvSummaKuukauteenAstiObservable: Observable<number> = combineLatest([
      this.valittuKuukausiObservable,
      this._asiakasObservable,
      this.latestAlvIlmoitusObservable,
      this.naytaAlvIlmoitusValilehtiObservable,
    ]).pipe(
      switchMap(([kuukausi, asiakas, alvIlmoitus, onkoVerokausi]) => {
        if (!kuukausi || !asiakas || !alvIlmoitus || onkoVerokausi === undefined) {
          return observableOf<number>(null)
        }
        const jaksotiedot = this._kirjanpitoJaettuService.annaAlvIlmoituksenJaksotiedot(kuukausi, asiakas)
        const onkoPitkaJakso = jaksotiedot?.jakso === AlvIlmoitusjakso.KK3 || jaksotiedot?.jakso === AlvIlmoitusjakso.KK12
        if (!onkoPitkaJakso || onkoVerokausi) {
          return observableOf(this._getAlvSum(alvIlmoitus))
        }
        const kuukaudenViimeinen = this._dateService.kuukaudenViimeinenPaikallinen({ year: kuukausi.year, month: kuukausi.month, day: 1 })
        const alku = this._dateService.localDateToNumber(jaksotiedot.aikavali.start)
        const loppu = this._dateService.localDateToNumber(kuukaudenViimeinen)
        const collectionUri = this._kirjanpitoUriService.annaRaporttikirjausCollectionUri(asiakas.avain)
        return this._firebaseLemonator
          .firestoreCollection<Raportointikirjaus>(collectionUri)
          .where('p', '>', alku - 1)
          .where('p', '<', loppu + 1)
          .listen()
          .pipe(
            map(kirjaukset => {
              const summat = this._kirjanpitoJaettuService.laskeAlvIlmoituksenSummatKirjauksistaKuukauteenAsti(kirjaukset, kuukausi, asiakas)
              const kuukauteenAsti = this._kirjanpitoJaettuService.annaUusiAlvilmoitus()
              this._kirjanpitoJaettuService.copyValuesFromSumsToAlvIlmoitus(kuukauteenAsti, summat)
              return this._kirjanpitoJaettuService.annaMaksettavaTaiPalautettavaVero(kuukauteenAsti)
            }),
          )
      }),
      lemonShare(),
    )

    const vatAutotextObservable = combineLatest([
      raportitFormsObservable,
      this.valittuKuukausiObservable,
      this._asiakasObservable,
      this.latestAlvIlmoitusObservable,
      this.naytaAlvIlmoitusValilehtiObservable,
      apixSettingsObservable,
      alvSummaKuukauteenAstiObservable,
    ]).pipe(
      map(([selectedLiitteet, kuukausi, asiakas, alvIlmoitus, onkoVerokausi, apixSettings, alvSummaKuukauteenAsti]) => {
        if (!kuukausi || !asiakas || !alvIlmoitus || onkoVerokausi === undefined) {
          return null
        }

        const kieli = asiakas.laskunKieli || 'fi'
        const clientType: 'holvi' | 'regular' = this._kirjanpitoJaettuService.getClientTypeAsString(asiakas)
        const kuukaudenNimi = this._dateService.annaKuukaudenNimiPaikallinen(kuukausi, kieli)

        // Format liitteet list (final two should have ' ja ' between them)
        const liitteetFormatted: string = this._createLiitteetListText(selectedLiitteet, kieli, clientType)

        const kuunEnsimmainen: LocalDate = { year: kuukausi.year, month: kuukausi.month, day: 1 }
        const nykyinenIlmoitusjakso = this._asiakasJaettuService.annaNykyinenAlvIlmoitusjaksoPaivalle(asiakas, this._dateService.localDateToDate(kuunEnsimmainen))
        const seuraavaIlmoitusjakso = this._asiakasJaettuService.annaSeuraavaAlvIlmoitusjakso(asiakas, nykyinenIlmoitusjakso)

        // Standard text for clients with monthly VAT-period set as default
        let mailTextRoot = 'kirjanpito-lahetys.' + clientType + '.mail-text-monthly-alv-jakso.'
        if (nykyinenIlmoitusjakso.alvIlmoitusjakso === AlvIlmoitusjakso.KK3 || nykyinenIlmoitusjakso.alvIlmoitusjakso === AlvIlmoitusjakso.KK12) {
          // Set text by whether the chosen month is the final month of the VAT period or not
          mailTextRoot = 'kirjanpito-lahetys.' + clientType + (onkoVerokausi ? '.mail-text-alv-month.' : '.mail-text-not-alv-month.')
        }

        const alvSum = alvSummaKuukauteenAsti ?? this._getAlvSum(alvIlmoitus)
        const vatMailText: string = mailTextRoot + this._getTextFromAlvSum(alvSum)

        const dueDate = this._kirjanpitoJaettuService.annaAlvnErapaiva(clientType, kuukausi, nykyinenIlmoitusjakso, seuraavaIlmoitusjakso, this._dateService.currentLocalDate())
        const weekDayName = this._dateService.annaPaivanNimiPaikallinen(dueDate, kieli).toLowerCase()
        const dueDateStr = this._dateService.muotoilePaikallinenPaiva(dueDate, kieli)

        // Return text for VAT client
        const vatClientTeksti = this._translationService.lokalisoi(vatMailText, kieli, {
          kuukaudenNimi: kuukaudenNimi,
          liitteetText: liitteetFormatted,
          alvSum: this._currencyService.formatoiRahaIlmanValuuttaSymbolia(alvSum > 0 ? alvSum : -1 * alvSum, kieli),
          weekdayOfDueDate: weekDayName,
          dueDate: dueDateStr,
        })

        // Verohallinnon viitenumerot muutetaan 18.11.2025 alkaen, ilmoitetaan muutoksesta 1.12.2025 alkaen.
        // Ilmoitetaan asiasta ei-TMI asiakkaalle 3, 9 tai 12 kuukautta päivityksestä riippuen nykyisestä ALV-ilmoitusjaksosta.
        // https://lemontree-squad.monday.com/boards/6845117610/pulses/18287165276
        // TODO: Tämä koodi voidaan poistaa 01.12.2026 jälkeen
        const viitenumeroPaivitysPvm = new Date('2025-12-01T00:00:00Z')
        const nyt = new Date()
        const asiakkanYritysmuotoOy = asiakas.yritysmuoto === Yritysmuoto.OSAKEYHTIO
        let ilmoitaViitenumeroMuutos = false

        if (asiakkanYritysmuotoOy && nyt >= viitenumeroPaivitysPvm && nyt < this._dateService.lisaaKuukausia(viitenumeroPaivitysPvm, 12)) {
          const nykyinenAlvIlmoitusJakso = this._asiakasJaettuService.annaNykyinenAlvIlmoitusjakso(asiakas)
          if (nykyinenAlvIlmoitusJakso === AlvIlmoitusjakso.KK1) {
            const kolmeKuukauttaPaivityksesta = this._dateService.lisaaKuukausia(viitenumeroPaivitysPvm, 3)
            if (nyt < kolmeKuukauttaPaivityksesta) {
              ilmoitaViitenumeroMuutos = true
            }
          } else if (nykyinenAlvIlmoitusJakso === AlvIlmoitusjakso.KK3) {
            const yhdeksanKuukauttaPaivityksesta = this._dateService.lisaaKuukausia(viitenumeroPaivitysPvm, 9)
            if (nyt < yhdeksanKuukauttaPaivityksesta) {
              ilmoitaViitenumeroMuutos = true
            }
          } else if (nykyinenAlvIlmoitusJakso === AlvIlmoitusjakso.KK12) {
            const kaksitoistaKuukauttaPaivityksesta = this._dateService.lisaaKuukausia(viitenumeroPaivitysPvm, 12)
            if (nyt < kaksitoistaKuukauttaPaivityksesta) {
              ilmoitaViitenumeroMuutos = true
            }
          }
        }

        // If there is VAT, add text to reflect that
        if (alvSum && alvSum > 0 && !asiakas.estaAlvVerkkolaskujenLahetys && onkoVerokausi) {
          // TODO: Tämä teksti voidaan poistaa 01.12.2026 jälkeen
          const viitenumeroMuutosTeksti = ilmoitaViitenumeroMuutos
            ? `\n\n<strong>${this._translationService.lokalisoi('kirjanpito-lahetys.alv-verkkolasku-viitenumero-muutos-muistutus', kieli)}</strong>`
            : ''

          if (
            ((asiakas.sahkoinenLaskutusosoite?.sahkoinenOsoite && asiakas.sahkoinenLaskutusosoite?.sahkoinenValittaja) ||
              (apixSettings?.forwardActive && apixSettings?.forwardAddress?.sahkoinenOsoite && apixSettings?.forwardAddress?.sahkoinenValittaja && apixSettings?.forwardLemontreeLaskut)) &&
            !apixSettings?.paymentReceiveIsActive
          ) {
            const dateToCompareWith = alvIlmoitus.lahetetty ? this._dateService.timestampToLocalDate(alvIlmoitus.lahetetty) : this._dateService.currentLocalDate()

            let dueDateWarningText = null
            if (this._dateService.paiviaValissaPaikallinen(dueDate, dateToCompareWith) < 5) {
              dueDateWarningText = '\n' + this._translationService.lokalisoi('kirjanpito-lahetys.teknisista-syista-erapaiva', kieli)
            }
            return vatClientTeksti + '\n' + this._translationService.lokalisoi('kirjanpito-lahetys.alv-verkkolasku-lahetetty', kieli) + (dueDateWarningText ?? '') + viitenumeroMuutosTeksti
          } else if (apixSettings?.paymentReceiveIsActive && apixSettings?.forwardLemontreeLaskut) {
            return vatClientTeksti + '\n' + this._translationService.lokalisoi('kirjanpito-lahetys.alv-verkkolasku-lahetetty-payments', kieli) + viitenumeroMuutosTeksti
          } else {
            return vatClientTeksti + viitenumeroMuutosTeksti
          }
        }

        return vatClientTeksti
      }),
    )

    const mainAutotextObservable = combineLatest([this.valittuKuukausiObservable, this._asiakasObservable]).pipe(
      switchMap(([kuukausi, asiakas]) => {
        const kuunEnsimmainen: LocalDate = { year: kuukausi.year, month: kuukausi.month, day: 1 }
        const nykyinenIlmoitusjakso = this._asiakasJaettuService.annaNykyinenAlvIlmoitusjaksoPaivalleIlmanObjektia(asiakas, this._dateService.localDateToDate(kuunEnsimmainen))
        if (nykyinenIlmoitusjakso === AlvIlmoitusjakso.EI || nykyinenIlmoitusjakso === AlvIlmoitusjakso.TUNTEMATON) {
          // console.log('Return NON-VAT')
          return nonVatAutotextObservable
        }
        // console.log('Return VAT')
        return vatAutotextObservable
      }),
    )

    mainAutotextObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(text => {
      this.mainAutotext = text
      this._changeDetectorRef.markForCheck()
      this._changeDetectorRef.detectChanges()
    })

    combineLatest([this.valittuKuukausiObservable, this._asiakasObservable])
      .pipe(
        switchMap(([kuukausi, asiakas]) => {
          if (!kuukausi || !asiakas) {
            return observableOf<KirjanpitoEmailTextAutosaveData>(null)
          }
          const uri = this._kirjanpitoUriService.annaKirjanpitoEmailTekstiAutosaveUri(asiakas.avain, kuukausi)
          return this._firebaseLemonator
            .firestoreDoc<KirjanpitoEmailTextAutosaveData>(uri)
            .listen()
            .pipe(
              take(1),
              map(autosaveData => {
                const output: Partial<KirjanpitoEmailTextAutosaveData> = {}

                output.lahetysAvain = autosaveData?.lahetysAvain

                // If regular client: piilokopio turned on by default and then set according to autosaved value
                // If Holvi client: piilokopio is always turned off
                const clientType: 'holvi' | 'regular' = this._kirjanpitoJaettuService.getClientTypeAsString(asiakas)
                if (clientType === 'holvi') {
                  output.piilokopioValittu = false
                  this._asetaKaytostaPois(this.lomake.piilokopioLahettajalle, this._piilokopioPoissa, true)
                } else {
                  this._asetaKaytostaPois(this.lomake.piilokopioLahettajalle, this._piilokopioPoissa, false)
                  output.piilokopioValittu = autosaveData?.piilokopioValittu ?? true
                }

                const date = this._dateService.localDateToDate({ year: kuukausi.year, month: kuukausi.month, day: 1 })
                const alvKausi = this._asiakasJaettuService.annaNykyinenAlvIlmoitusjaksoPaivalleIlmanObjektia(asiakas, date)
                const alvDisabled = alvKausi === AlvIlmoitusjakso.EI || alvKausi === AlvIlmoitusjakso.TUNTEMATON
                if (alvDisabled) {
                  asetaArvo(this.lomake.alvIlmoitus, false)
                  this._asetaKaytostaPois(this.lomake.alvIlmoitus, this._alvIlmoitusPoissa, true)
                } else {
                  this._asetaKaytostaPois(this.lomake.alvIlmoitus, this._alvIlmoitusPoissa, false)
                }

                // Autosave-dokumentti voi sisältää vain osan ruksista (kirjoittajat käyttävät distinctUntilChanged),
                // joten jokaiselle puuttuvalle kentälle täytyy täyttää oletus erikseen
                const paakirjanOletus = asiakas.kirjanpitoraporttienLahetystiedot?.liitaPaakirja ?? false
                const tuloksenOletus = clientType === 'holvi' ? (asiakas.kirjanpitoraporttienLahetystiedot?.liitaTuloslaskelma ?? false) : true
                const taseenOletus = clientType === 'holvi' ? (asiakas.kirjanpitoraporttienLahetystiedot?.liitaTase ?? false) : true
                // KK3- ja KK12-asiakkaalla alv-laskelma hämää saajaa muina kuin kauden viimeisenä kuukautena, joten oletuksena rasti on pois
                const alvinOletus = this._onkoAlvKaudenViimeinenKuukausi(kuukausi, asiakas, alvKausi)

                output.paakirjaValittu = autosaveData?.paakirjaValittu ?? paakirjanOletus
                output.tulosValittu = autosaveData?.tulosValittu ?? tuloksenOletus
                output.taseValittu = autosaveData?.taseValittu ?? taseenOletus
                output.alvValittu = alvDisabled ? false : (autosaveData?.alvValittu ?? alvinOletus)
                output.selvOstotValittu = autosaveData?.selvOstotValittu
                output.selvMyynnitValittu = autosaveData?.selvMyynnitValittu

                return output
              }),
            )
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(autosaved => {
        if (!autosaved) {
          return
        }
        this._lahetysAvainSubject.next(autosaved.lahetysAvain || this._firebaseLemonator.firestoreCreateId())
        this._setCheckboxesFromAutosave(autosaved)
      })

    combineLatest([this._asiakasObservable, muutokset(this.lomake.selvitettavatOstot), muutokset(this.lomake.selvitettavatMyynnit)])
      .pipe(
        map(([asiakas, ostotValittu, myynnitValittu]) => {
          if (!asiakas) {
            return ''
          }
          return this._getSelvitettavatText(asiakas, ostotValittu, myynnitValittu)
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(text => {
        this.selvitettavatAutotext = text
      })

    combineLatest([
      this._asiakasService.nykyinenAsiakasAvainObservable,
      this.valittuKuukausiObservable,
      this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable,
      this._kayttajaService.kirjanpitajaOnDevaajaObservable,
    ])
      .pipe(
        switchMap(([asiakas, selectedMonth, kirjanpitajatMap, onkoDevaaja]) => {
          if (asiakas && selectedMonth) {
            this.historyLoading = true
            const uri = this._kirjanpitoUriService.annaKirjanpitoEmailLahetyksetCollection(asiakas.avain)
            return this._firebaseLemonator
              .firestoreCollection<KirjanpitoEmailLahetys>(uri)
              .where('tunniste', '==', selectedMonth.month + '_' + selectedMonth.year)
              .listen()
              .pipe(
                map(lahetykset => {
                  const sortedLahetykset = lahetykset.sort((a, b) => b.luotu.toMillis() - a.luotu.toMillis())
                  return sortedLahetykset.map(lahetys => {
                    const kirjanpitajaData = kirjanpitajatMap.get(lahetys.luoja)
                    const historyEntry: LahetysHistoria = Object.assign({}, lahetys, {
                      recipientsEmails: lahetys.recipients.reduce((a, b) => a + b.email + ' ', ''),
                      liitteet: [],
                      nimi: kirjanpitajaData.etunimi + ' ' + kirjanpitajaData.sukunimi,
                      luotuDateStr: this._dateService.muotoilePaivaJaAikaDate(this._dateService.timestampToDate(lahetys.luotu), 'fi'),
                    })
                    if (onkoDevaaja) {
                      // Debug thingies
                      const debugUri = this._kirjanpitoUriService.annaKirjanpitoEmailLahetyksetLahetysAvaimella(asiakas.avain, historyEntry.avain)
                      historyEntry.firestoreLinkki = debugUri
                      historyEntry.firestoreLinkkiEncoded = this._debugService.createFirestoreLink(debugUri)
                    }
                    // historyEntry.teksti = historyEntry.teksti.replace(/<br\s*\/?>/gi, '\n')
                    return historyEntry
                  })
                }),
              )
          }
          return observableOf<LahetysHistoria[]>([])
        }),
        tap(data => {
          this.onkoSpostejaLahetetty.emit(data && data.length > 0)
        }),
        withLatestFrom(this._asiakasService.nykyinenAsiakasAvainObservable, this._kayttajaService.kirjanpitajaOnDevaajaObservable),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(async ([lahetysHistoriat, asiakasAvaintiedot, onkoDevaaja]) => {
        if (!lahetysHistoriat?.length) {
          this.sentThisMonth = []
          this.historyLoading = false
          return
        }
        const liitteetPromises: Promise<void>[] = []
        for (const historia of lahetysHistoriat) {
          const promise = this._annaLahetyksenLiitteet(asiakasAvaintiedot, historia, onkoDevaaja)
          liitteetPromises.push(promise)
        }
        await Promise.all(liitteetPromises)

        this.sentThisMonth = lahetysHistoriat
        this.historyLoading = false
      })
  }

  // defer on pakollinen: nämä streamit tilataan uudelleen aina kun kuukausi tai asiakas vaihtuu,
  // ja ilman sitä startWith palauttaisi ruksin arvon streamin luontihetkeltä (= liitelista tyhjenisi vaikka ruksit ovat päällä)
  private _annaArvoJaMuutokset(kentta: FieldTree<boolean>): Observable<boolean> {
    return defer(() => arvonMuutokset(kentta, { injector: this._injector }).pipe(startWith(kentta().value())))
  }

  private _asetaKaytostaPois(kentta: FieldTree<boolean>, poissa: WritableSignal<boolean>, onPoissa: boolean) {
    poissa.set(onPoissa)
    // Reaktiivisen lomakkeen disable() ja enable() lähettivät kentän nykyisen arvon valueChanges-tilaajille
    asetaArvo(kentta, kentta().value())
  }

  private _onkoAlvKaudenViimeinenKuukausi(kuukausi: LocalMonth, asiakas: Asiakas, alvKausi: AlvIlmoitusjakso): boolean {
    if (!alvKausi || alvKausi === AlvIlmoitusjakso.EI || alvKausi === AlvIlmoitusjakso.TUNTEMATON) {
      return false
    }
    const jaksotiedot = this._kirjanpitoJaettuService.annaAlvIlmoituksenJaksotiedot(kuukausi, asiakas)
    if (!jaksotiedot) {
      return false
    }
    return jaksotiedot.aikavali.end.year === kuukausi.year && jaksotiedot.aikavali.end.month === kuukausi.month
  }

  private _annaLahetyksenLiitteet(asiakasAvaintiedot: AsiakkaanAvainTiedot, historia: LahetysHistoria, onkoDevaaja: boolean): Promise<void> {
    const uri = this._kirjanpitoUriService.annaKirjanpitoEmailLahetyksenLiitteetCollection(asiakasAvaintiedot.avain, historia.avain)
    return this._firebaseLemonator
      .firestoreCollection<HistoriaEmailLiite>(uri)
      .where('poistettu', '==', false)
      .get()
      .then(liitteet => {
        historia.liitteet = liitteet || []
        if (onkoDevaaja) {
          // Debug thingies
          for (const liite of historia.liitteet) {
            const debugUri = this._kirjanpitoUriService.annaKirjanpitoEmailLahetyksenLiitteenUri(asiakasAvaintiedot.avain, historia.avain, liite.avain)
            liite.firestoreLinkki = debugUri
            liite.firestoreLinkkiEncoded = this._debugService.createFirestoreLink(debugUri)
          }
        }
      })
  }

  // async reload() {
  //   const asiakas = await firstValueFrom(this._asiakasObservable)
  //   const kuukausi = await firstValueFrom(this.valittuKuukausiObservable)
  //   const uri = this._kirjanpitoUriService.annaKirjanpitoEmailTekstiAutosaveUri(asiakas.avain, kuukausi)
  //   await this._firebaseLemonator.firestoreDeleteDoc(uri)
  // }

  private _annaAihe(autosaved: KirjanpitoEmailTextAutosaveData, kuukausi: LocalMonth, asiakas: Asiakas, kieli: TuettuKieli) {
    if (autosaved) {
      if (autosaved.aiheet && autosaved.aiheet[kieli]) {
        return autosaved.aiheet[kieli]
      }
      if (kieli === 'fi' && autosaved.aihe) {
        return autosaved.aihe
      }
    }

    const kuukaudenNimi = this._dateService.annaKuukaudenNimiPaikallinen(kuukausi, kieli)
    const kuukaudenNimiFormatted = kieli === 'en' ? kuukaudenNimi.substring(0, 1).toUpperCase() + kuukaudenNimi.substring(1).toLowerCase() : kuukaudenNimi.toLowerCase()
    return `${asiakas.nimi}, ${kuukaudenNimiFormatted} ${kuukausi.year}`
  }

  private _getTextStartFromAutosaveData(autosaved: KirjanpitoEmailTextAutosaveData, kieli: TuettuKieli, clientType: 'holvi' | 'regular'): string {
    if (autosaved) {
      if (autosaved.tekstinAlut && autosaved.tekstinAlut[kieli]) {
        return autosaved.tekstinAlut[kieli]
      }
      if (kieli === 'fi' && autosaved.tekstinAlku) {
        return autosaved.tekstinAlku
      }
    }

    return this._translationService.lokalisoi('kirjanpito-lahetys.' + clientType + '.input-start', kieli)
  }

  private _getTextEndFromAutosaveData(
    autosaved: KirjanpitoEmailTextAutosaveData,
    kieli: TuettuKieli,
    clientType: 'holvi' | 'regular',
    kirjanpitajanTiedot: KirjanpitajanTiedot,
    nimitiedotMap: Map<string, any>,
  ): string {
    if (autosaved) {
      if (autosaved.tekstinLoput && autosaved.tekstinLoput[kieli]) {
        return autosaved.tekstinLoput[kieli]
      }
      if (kieli === 'fi' && autosaved.tekstinLoppu) {
        return autosaved.tekstinLoppu
      }
    }

    const emailEndText = this._getEmailEndText(kieli, clientType, kirjanpitajanTiedot, nimitiedotMap)
    return this._translationService.lokalisoi('kirjanpito-lahetys.' + clientType + '.input-end', kieli) + (emailEndText ? '\n' + emailEndText : '')
  }

  private _getEmailEndText(kieli: TuettuKieli, clientType: 'holvi' | 'regular', kirjanpitajanTiedot: KirjanpitajanTiedot, nimitiedotMap: Map<string, any>): string {
    if (clientType === 'regular' && nimitiedotMap && kirjanpitajanTiedot.uid) {
      return nimitiedotMap.get(kirjanpitajanTiedot.uid)?.emailEndText?.[kieli] ?? ''
    }
    return ''
  }

  private _updateAutosaveData(data: Partial<KirjanpitoEmailTextAutosaveData>, kuukausi: LocalMonth, asiakas: Asiakas): Promise<void> {
    if (!kuukausi || !asiakas) {
      return Promise.resolve()
    }
    const uri = this._kirjanpitoUriService.annaKirjanpitoEmailTekstiAutosaveUri(asiakas.avain, kuukausi)
    data.paivitettu = this._timestampService.now()
    return this._firebaseLemonator.firestoreSetData(uri, data, { merge: true })
  }

  async _resetAutosave(): Promise<void> {
    const asiakasAvainTiedotPromise = firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
    const kuukausiPromise = firstValueFrom(this.valittuKuukausiObservable)

    const [asiakasAvainTiedot, kuukausi] = await Promise.all([asiakasAvainTiedotPromise, kuukausiPromise])

    const uri = this._kirjanpitoUriService.annaKirjanpitoEmailTekstiAutosaveUri(asiakasAvainTiedot.avain, kuukausi)
    return this._firebaseLemonator.firestoreDeleteDoc(uri)
  }

  private _getAlvSum(latestAlv: AlvIlmoitus): number {
    if (latestAlv) {
      return this._kirjanpitoJaettuService.annaMaksettavaTaiPalautettavaVero(latestAlv)
    }
    return 0
  }

  // get vastaanottaja() {
  //   return this.lahetysForm.get('vastaanottaja')
  // }
  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }
  // lisaaVastaanottaja() {
  //   const field = this.vastaanottaja
  //   field.updateValueAndValidity()
  //   this.lisaaEmail(field)
  // }
  // poistaVastaanottaja(email: string): void {
  //   const index = this.vastaanottajat.findIndex(v => v.email === email)
  //   if (index >= 0) {
  //     this.vastaanottajat.splice(index, 1)
  //   }
  // }

  validateEmail: ValidatorFn = (ctrl: AbstractControl): ValidationErrors | null => {
    if (ctrl.value == null || ctrl.value === undefined || ctrl.value.trim() === '') {
      return null
    }
    return Validators.email(ctrl)
  }

  async laheta() {
    // Avoid multiple sends
    if (this._sendingInProgress) {
      return
    }

    this._sendingInProgress = true

    if (!this.vastaanottajat?.length) {
      this._snackbar.open('Sähköpostia ei voi lähettää. Asiakkaalla ei ole aktiivisia yhteyshenkilöitä.', 'OK', { duration: 10000 })
      return
    }
    if (!this.lomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this._ladataanService.aloitaLataaminen()

    try {
      // Obtain required user, etc. data
      const asiakas = await firstValueFrom(this._asiakasObservable)
      const selectedMonth = await firstValueFrom(this.valittuKuukausiObservable)
      const tallentavaKirjanpitaja = await this._kayttajaService.getKirjanpitajanTiedot()
      const latestAlvIlmoitus = await firstValueFrom(this.latestAlvIlmoitusObservable)
      const mailText = this.combineMailText()

      if (!asiakas) {
        throw new Error('Ei asiakasta tallennettaessa!')
      }

      if (!selectedMonth) {
        throw new Error('Ei valittua kuukautta tallennettaessa!')
      }

      if (!tallentavaKirjanpitaja) {
        throw new Error('Ei tallentavaa kirjanpitäjää tallennettaessa!')
      }

      const date = this._dateService.localDateToDate({ year: selectedMonth.year, month: selectedMonth.month, day: 1 })
      const alvKausi = this._asiakasJaettuService.annaNykyinenAlvIlmoitusjaksoPaivalleIlmanObjektia(asiakas, date)
      const noAlv = alvKausi === AlvIlmoitusjakso.EI || alvKausi === AlvIlmoitusjakso.TUNTEMATON
      if (!noAlv && this.lomake.alvIlmoitus().value() && !latestAlvIlmoitus.endDate) {
        throw new Error('Asiakas asiakkaat/' + asiakas.avain + ':  No end date for alv-ilmoitus ' + JSON.stringify(latestAlvIlmoitus, null, '  '))
      }

      // Create saved documents
      const aika = this._timestampService.now()
      const lahetysAvain: string = this._lahetysAvainSubject.value
      const tyojonoId: string = this._firebaseLemonator.firestoreCreateId()
      const tyojonoData: KirjanpitoEmailLahetysTyojono = {
        luotu: this._timestampService.now(),
        asiakasAvain: asiakas.avain,
        kirjanpitoEmailLahetysAvain: lahetysAvain,
      }
      const recipientStatuses: EmailLahetysStatus[] = []
      for (const recipient of this.vastaanottajat) {
        const emailLahetysStatus: EmailLahetysStatus = {
          email: recipient,
          status: EmailLahetysStatusKoodi.PROSESSOIDAAN,
          viesti: null,
        }
        recipientStatuses.push(emailLahetysStatus)
      }
      const tekstiWithBreaks = mailText.replace(/(?:\r\n|\r|\n)/g, '<br/>')
      const lahetysData: KirjanpitoEmailLahetys = {
        avain: lahetysAvain,
        kuukausi: selectedMonth,
        tunniste: selectedMonth.month + '_' + selectedMonth.year,
        luoja: tallentavaKirjanpitaja.uid,
        luotu: aika,
        lahetetty: null,
        lisaaPiilokopio: this.lomake.piilokopioLahettajalle().value(),
        recipients: recipientStatuses,
        kieli: asiakas.laskunKieli || 'fi',
        aihe: this.lomake.aihe().value(),
        teksti: tekstiWithBreaks,
        liitaTuloslaskelma: this.lomake.tuloslaskelma().value(),
        liitaTase: this.lomake.tase().value(),
        liitaPaakirja: this.lomake.paakirja().value(),
        liitaAlvIlmoitus: this.lomake.alvIlmoitus().value(),
        liitaSelvitettavatOstot: this.lomake.selvitettavatOstot().value(),
        liitaSelvitettavatMyynnit: this.lomake.selvitettavatMyynnit().value(),
        alvIlmoitus: noAlv ? null : latestAlvIlmoitus || null,
      }

      // Emergency break to avoid empty email sends
      if (!lahetysData.aihe.replace(/\s/g, '') || !lahetysData.teksti.replace(/(?:<br\/>|\s)/g, '')) {
        this._ladataanService.lopetaLataaminen()
        this._sendingInProgress = false
        this._snackbar.open('Lähetys keskeytetty! Aihe tai teksti puuttuu', 'OK', { duration: 5000, horizontalPosition: 'center', verticalPosition: 'bottom' })
        return
      }

      const asiakasUri = 'asiakkaat/' + asiakas.avain
      const lahetysUri = this._kirjanpitoUriService.annaKirjanpitoEmailLahetyksetLahetysAvaimella(asiakas.avain, lahetysAvain)
      const tyojonoUri = this._kirjanpitoUriService.annaKirjanpitoEmailLahetysTyojonoEnsimmainenUri(asiakas.avain, tyojonoId)

      const lahetysTiedot: KirjanpitoraporttienLahetystiedot = {
        // talousraporttienVastaanottajat: this.talousraporttienVastaanottajat,
        liitaTuloslaskelma: lahetysData.liitaTuloslaskelma,
        liitaTase: lahetysData.liitaTase,
        liitaPaakirja: lahetysData.liitaPaakirja,
        liitaAlvIlmoitus: lahetysData.liitaAlvIlmoitus,
        liitaSelvitettavatOstot: lahetysData.liitaSelvitettavatOstot,
        liitaSelvitettavatMyynnit: lahetysData.liitaSelvitettavatMyynnit,
      }

      // Update values going to the history version too
      asiakas.kirjanpitoraporttienLahetystiedot = lahetysTiedot
      asiakas.paivitetty = this._timestampService.now()
      asiakas.paivittaja = tallentavaKirjanpitaja.uid

      // Add to batch
      const batch = this._firebaseLemonator.firestoreBatch()
      this._asiakasService.lisaaAsiakkaanHistoriaBatchiin(asiakas, batch)
      const asiakasDocToUpdate: Partial<Asiakas> = {
        kirjanpitoraporttienLahetystiedot: lahetysTiedot,
        paivitetty: asiakas.paivitetty,
        paivittaja: asiakas.paivittaja,
      }
      batch.update(asiakasUri, asiakasDocToUpdate)
      batch.set(tyojonoUri, tyojonoData)
      batch.set(lahetysUri, lahetysData)

      // Save
      await batch.commit()

      this._ladataanService.lopetaLataaminen()
      this._snackbar.open('Sähköposti lähetettiin onnistuneesti.', 'OK', { duration: 5000 })
      await this._resetAutosave()
      this._lahetysAvainSubject.next(this._firebaseLemonator.firestoreCreateId())
    } catch (error) {
      this._errorHandler.handleError(error)
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._sendingInProgress = false
    }
  }

  async downloadSentReport(lahetysAvain: string, reportType: RaporttiName) {
    this._ladataanService.aloitaLataaminen()
    try {
      const asiakas = await firstValueFrom(this._asiakasObservable)
      if (!asiakas) {
        return
      }

      const downloadRequest: KirjanpitoEmailLahetysHistoryPdfRequest = {
        asiakasAvain: asiakas.avain,
        reportType: reportType,
        lahetysAvain: lahetysAvain,
      }
      this._firebaseLemonator.functionsCall<KirjanpitoEmailLahetysHistoryPdfRequest, KirjanpitoEmailLahetysHistoryPdfResponse>('kirjanpitoEmailLahetysDownloadPdf', downloadRequest).then(resp => {
        if (!resp || resp.e) {
          throw new Error('Failed to download attachment! ' + resp?.e || 'No response')
        }
        if (!resp.base64File) {
          throw new Error('Base64 missing!')
        }
        const nimi = asiakas.nimi + '_' + reportType + '_' + lahetysAvain + '.pdf'
        this._fileSaverService.saveBase64As(resp.base64File, nimi, 'pdf')
      })
    } catch (error) {
      this._errorHandler.handleError(error)
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async downloadAttachment(liite: EmailLiite) {
    this._ladataanService.aloitaLataaminen()
    try {
      const asiakas = await firstValueFrom(this._asiakasObservable)
      if (!asiakas) {
        return
      }

      const downloadRequest: EmailLiiteDownloadRequest = {
        asiakasAvain: asiakas.avain,
        liite: liite,
      }
      await this._firebaseLemonator.functionsCall<EmailLiiteDownloadRequest, EmailLiiteDownloadResponse>('kirjanpitoDownloadEmailLiite', downloadRequest).then(resp => {
        if (!resp || resp.e) {
          throw new Error('Failed to download attachment! ' + resp?.e || 'No response')
        }
        if (!resp.base64File) {
          throw new Error('Base64 missing!')
        }
        this._fileSaverService.saveBase64AsGuessedType(resp.base64File, liite.nimi)
      })
    } catch (error) {
      this._errorHandler.handleError(error)
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  // private lisaaEmail(field: AbstractControl) {
  //   if ((field.value || '').trim()) {
  //     let arvo = field.value.trim()
  //     arvo = arvo.replace(/,/g, '').trim()
  //     if (!new UntypedFormControl(arvo, [Validators.email]).errors) {
  //       if (!this.talousraporttienVastaanottajat) {
  //         this.talousraporttienVastaanottajat = []
  //       }
  //       this.talousraporttienVastaanottajat.push(arvo)
  //       field.setValue('')
  //     }
  //   }
  // }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  public downloadTulos() {
    this._downloadPdf(RaporttiType.TULOS)
  }
  public downloadTase() {
    this._downloadPdf(RaporttiType.TASE)
  }
  public downloadPaakirja() {
    this._downloadPdf(RaporttiType.PAAKIRJA)
  }
  public downloadAlvIlmoitus() {
    this._downloadPdf(RaporttiType.ALV_ILMOITUS)
  }
  public downloadSelvitettavatOstot() {
    this._downloadPdf(RaporttiType.SELVITETTAVAT_OSTOT)
  }
  public downloadSelvitettavatMyynnit() {
    this._downloadPdf(RaporttiType.SELVITETTAVAT_MYYNNIT)
  }

  private async _downloadPdf(type: RaporttiType) {
    this._ladataanService.aloitaLataaminen()

    const asiakas = await firstValueFrom(this._asiakasObservable)
    const valittuKuukausi = await firstValueFrom(this.valittuKuukausiObservable)

    if (!asiakas || !valittuKuukausi) {
      this._ladataanService.lopetaLataaminen()
      return
    }

    const latestAlvIlmoitusPromise = firstValueFrom(this.latestAlvIlmoitusObservable)

    const valitunKuukaudenAlku: LocalDate = { year: valittuKuukausi.year, month: valittuKuukausi.month, day: 1 }
    const valitunKuukaudenLoppu = this._dateService.kuukaudenViimeinenPaikallinen(valitunKuukaudenAlku)

    const pyynto: RaporttiRequest = {
      a: asiakas.avain,
      k: asiakas.laskunKieli || 'fi',
      w: type,
      s: this._dateService.localDateToNumber(valitunKuukaudenAlku),
      e: this._dateService.localDateToNumber(valitunKuukaudenLoppu),
      datasource: 'nippudokumentit',
    }

    if (type === RaporttiType.ALV_ILMOITUS) {
      pyynto.ai = await latestAlvIlmoitusPromise
    }

    try {
      return this._firebaseLemonator.functionsCall<RaporttiRequest, RaporttiPdfResponse>('kirjanpitoRaportitPdf', pyynto).then(data => {
        if (data.e) {
          this._ladataanService.lopetaLataaminen()
          switch (data.e) {
            case 'view-not-allowed': {
              this._snackbar.open('Käyttäjälla ei ole oikeutta nähdä tätä asiakasta.', 'OK')
              break
            }
            case 'ei-maksuyhteystietoja': {
              this._snackbar.open('Asiakkaalle ei ole lisätty veromaksuyhteystietoja', 'OK')
              break
            }
            case 'invalid-omaaloitteisetviite': {
              this._snackbar.open('Asiakkaan oma-aloitteisien verojen viitenumero on väärä tai puuttuu.', 'OK')
              break
            }
            default: {
              this._snackbar.open('Tietojen lataaminen epäonnistui.', 'OK')
            }
          }
        } else {
          this._ladataanService.lopetaLataaminen()
          if (data.base64File) {
            const fileName = data.fileName || 'raportti.pdf'
            this._fileSaverService.saveBase64As(data.base64File, fileName, 'pdf')
          } else {
            this._snackbar.open('PDF:in lataaminen epäonnistui.')
          }
        }
      })
    } catch (error) {
      this._ladataanService.lopetaLataaminen()
      this._errorHandler.handleError(error)
    }
  }

  async deleteAttachment(file: EmailLiite) {
    this._ladataanService.aloitaLataaminen()
    const asiakas = await firstValueFrom(this._asiakasObservable)

    const uri = this._kirjanpitoUriService.annaKirjanpitoEmailLahetyksenLiitteenUri(asiakas.avain, this._lahetysAvainSubject.value, file.avain)
    const markAsDeleted: Partial<EmailLiite> = { poistettu: true }
    await this._firebaseLemonator.firestoreUpdateData<Partial<EmailLiite>>(uri, markAsDeleted)
    this._ladataanService.lopetaLataaminen()
  }

  // Load start when clicked
  public async lataa(event: Event) {
    this.latausVirhe = null
    const inputTypeFile = event?.target as HTMLInputElement
    if (inputTypeFile?.files) {
      const list: FileList = inputTypeFile.files
      const tiedostot: FileDropEntry[] = this._tiedostojenLataamisService.fileListToNgxFileDropEntries(list)
      try {
        await this._uploadFile(tiedostot)
      } catch (err) {
        this.latausVirhe = 'Lataamisen aikana tapahtui virhe: ' + err
        this._errorHandler.handleError(err)
      }
    }
  }

  private async _uploadFile(tiedostot: FileDropEntry[]) {
    this._ladataanService.aloitaLataaminen()

    const asiakasPromise = firstValueFrom(this._asiakasObservable)
    const valittuKuukausiPromise = firstValueFrom(this.valittuKuukausiObservable)

    const [asiakas, valittuKuukausi] = await Promise.all([asiakasPromise, valittuKuukausiPromise])

    const batch = this._firebaseLemonator.firestoreBatch()

    for (const tiedosto of tiedostot) {
      const fileEnding = this._tiedostojenLataamisService.getFileEndingFromNgxFileDropEntry(tiedosto)

      const file = await this._tiedostojenLataamisService.getFile(tiedosto.fileEntry as FileSystemFileEntry)

      const avain = this._firebaseLemonator.firestoreCreateId()
      const metadata: EmailLiite = {
        avain: avain,
        nimi: file.name,
        koko: file.size,
        ladattu: this._timestampService.now(),
        fileType: fileEnding,
        poistettu: false,
      }

      const storageUri = this._kirjanpitoUriService.annaEmailLahetyksenLiiteStorageUri(asiakas.avain, metadata.avain, metadata.fileType)
      const uploadTask = this._tiedostojenLataamisService.upload(this._firebaseLemonator, storageUri, file)
      await firstValueFrom(uploadTask.doneObservable)

      const metadataUri = this._kirjanpitoUriService.annaKirjanpitoEmailLahetyksenLiitteenUri(asiakas.avain, this._lahetysAvainSubject.value, avain)
      batch.set<EmailLiite>(metadataUri, metadata)
    }

    const autosaveUri = this._kirjanpitoUriService.annaKirjanpitoEmailTekstiAutosaveUri(asiakas.avain, valittuKuukausi)
    const data: Partial<KirjanpitoEmailTextAutosaveData> = {
      lahetysAvain: this._lahetysAvainSubject.value,
    }
    batch.set<Partial<KirjanpitoEmailTextAutosaveData>>(autosaveUri, data, { merge: true })

    await batch.commit()

    this._ladataanService.lopetaLataaminen()
  }

  private _getTextFromAlvSum(alvSum: number): string {
    if (alvSum > 0) {
      return 'positive-alv'
    } else if (alvSum < 0) {
      return 'negative-alv'
    }
    return 'zero-alv'
  }
  private _createLiitteetListText(selectedLiitteet: string[], kieli: TuettuKieli, clientType: 'holvi' | 'regular'): string {
    if (!selectedLiitteet || !selectedLiitteet.length) {
      return ''
    }
    const commaSeparatedLiitteet = selectedLiitteet.join(', ')
    const finalComma = commaSeparatedLiitteet.lastIndexOf(', ')
    const liitteetList: string =
      selectedLiitteet.length > 1
        ? `${commaSeparatedLiitteet.substring(0, finalComma)} ${kieli === 'en' ? 'and' : 'ja'}${commaSeparatedLiitteet.substring(finalComma + 1)}` // add " ja", if more than one liite
        : selectedLiitteet.toString()

    return this._translationService.lokalisoi('kirjanpito-lahetys.' + clientType + '.liitteet-text', kieli, { liitteet: liitteetList })
  }

  private _getSelvitettavatText(asiakas: Pick<Asiakas, 'kasittelija' | 'laskunKieli' | 'yritysmuoto'>, ostotChecked: boolean, myynnitChecked: boolean): string {
    const clientType: 'holvi' | 'regular' = this._kirjanpitoJaettuService.getClientTypeAsString(asiakas)

    if (clientType === 'holvi') {
      return this._getHolviSelvitettavat(asiakas, ostotChecked, myynnitChecked)
    }
    return this._getRegularSelvitettavat(asiakas, ostotChecked, myynnitChecked)
  }

  private _getHolviSelvitettavat(asiakas: Pick<Asiakas, 'laskunKieli' | 'yritysmuoto'>, ostotChecked: boolean, myynnitChecked: boolean): string {
    const kieli = asiakas.laskunKieli || 'fi'
    if (!ostotChecked && !myynnitChecked) {
      return this._translationService.lokalisoi('kirjanpito-lahetys.holvi.ei-selvitettavia', kieli)
    }
    const selvitettaviaOstojaText = ostotChecked ? this._translationService.lokalisoi('kirjanpito-lahetys.holvi.selvitettavat-ostot', kieli) : ''
    const onkoSelvitettaviaMyyntejaText = myynnitChecked ? this._translationService.lokalisoi('kirjanpito-lahetys.holvi.selvitettavat-myynnit', kieli) : ''
    return selvitettaviaOstojaText + onkoSelvitettaviaMyyntejaText
  }

  private _getRegularSelvitettavat(asiakas: Pick<Asiakas, 'laskunKieli' | 'yritysmuoto'>, ostotChecked: boolean, myynnitChecked: boolean): string {
    const kieli = asiakas.laskunKieli || 'fi'
    if (!ostotChecked && !myynnitChecked) {
      return this._translationService.lokalisoi('kirjanpito-lahetys.regular.ei-selvitettavia', kieli)
    }
    if (ostotChecked && myynnitChecked) {
      if (asiakas.yritysmuoto === Yritysmuoto.TOIMINIMI || asiakas.yritysmuoto === Yritysmuoto.AVOINYHTIO || asiakas.yritysmuoto === Yritysmuoto.KOMMANDIITTIYHTIO) {
        return this._translationService.lokalisoi('kirjanpito-lahetys.regular.selvitettavat-ostot-ja-myynnit-tmi', kieli)
      }
      return this._translationService.lokalisoi('kirjanpito-lahetys.regular.selvitettavat-ostot-ja-myynnit-oy', kieli)
    }

    if (ostotChecked) {
      if (asiakas.yritysmuoto === Yritysmuoto.TOIMINIMI || asiakas.yritysmuoto === Yritysmuoto.AVOINYHTIO || asiakas.yritysmuoto === Yritysmuoto.KOMMANDIITTIYHTIO) {
        return this._translationService.lokalisoi('kirjanpito-lahetys.regular.selvitettavat-ostot-tmi', kieli)
      }
      return this._translationService.lokalisoi('kirjanpito-lahetys.regular.selvitettavat-ostot-oy', kieli)
    }

    if (myynnitChecked) {
      return this._translationService.lokalisoi('kirjanpito-lahetys.regular.selvitettavat-myynnit', kieli)
    }

    return ''
  }

  async tryAutosave() {
    const asiakasPromise = firstValueFrom(this._asiakasObservable)
    const kuukausiPromise = firstValueFrom(this.valittuKuukausiObservable)
    const kirjanpitajanTiedotPromise = this._kayttajaService.getKirjanpitajanTiedot()

    const aihe = this.lomake.aihe().value() || ''
    const alku = this.lomake.tekstinAlku().value() || ''
    const loppu = this.lomake.tekstinLoppu().value() || ''

    if (!aihe && !alku && !loppu) {
      return
    }

    const [asiakas, kirjanpitajanTiedot, kuukausi] = await Promise.all([asiakasPromise, kirjanpitajanTiedotPromise, kuukausiPromise])
    if (!asiakas || !kirjanpitajanTiedot || !kuukausi) {
      return
    }

    const kieli = asiakas.laskunKieli || 'fi'
    const autosaveUri = this._kirjanpitoUriService.annaKirjanpitoEmailTekstiAutosaveUri(asiakas.avain, kuukausi)
    const aiheet: Partial<{ [k in TuettuKieli]: string }> = {}
    const alut: Partial<{ [k in TuettuKieli]: string }> = {}
    const loput: Partial<{ [k in TuettuKieli]: string }> = {}
    aiheet[kieli] = aihe
    alut[kieli] = alku
    loput[kieli] = loppu
    const data: Partial<KirjanpitoEmailTextAutosaveData> = {
      aiheet: aiheet,
      tekstinAlut: alut,
      tekstinLoput: loput,
      paivittaja: kirjanpitajanTiedot.uid,
      paivitettu: this._timestampService.now(),
    }

    return this._firebaseLemonator.firestoreSetData(autosaveUri, data, { merge: true })
  }

  public showOrHideMainAutotext() {
    this.includeMainAutotext = !this.includeMainAutotext
  }
  public showOrHideSelvitettavatAutotext() {
    this.includeSelvitettavatAutotext = !this.includeSelvitettavatAutotext
  }

  private combineMailText(): string {
    let output = ''
    output += this.lomake.tekstinAlku().value() || ''
    if (this.includeMainAutotext) {
      output = output.trimEnd()
      output += '\n\n'
      output += this.mainAutotext || ''
    }
    if (this.includeSelvitettavatAutotext) {
      output = output.trimEnd()
      output += '\n\n'
      output += this.selvitettavatAutotext || ''
    }

    output = output.trimEnd()
    output += '\n\n'
    output += (this.lomake.tekstinLoppu().value() || '').trimStart()
    return output
  }

  private _setCheckboxesFromAutosave(autosaved: Partial<KirjanpitoEmailTextAutosaveData>): void {
    if (!autosaved) {
      return
    }

    if (autosaved.piilokopioValittu !== undefined && autosaved.piilokopioValittu !== null) {
      asetaArvo(this.lomake.piilokopioLahettajalle, autosaved.piilokopioValittu)
    }
    if (autosaved.paakirjaValittu !== undefined && autosaved.paakirjaValittu !== null) {
      asetaArvo(this.lomake.paakirja, autosaved.paakirjaValittu)
    }
    if (autosaved.taseValittu !== undefined && autosaved.taseValittu !== null) {
      asetaArvo(this.lomake.tase, autosaved.taseValittu)
    }
    if (autosaved.tulosValittu !== undefined && autosaved.tulosValittu !== null) {
      asetaArvo(this.lomake.tuloslaskelma, autosaved.tulosValittu)
    }
    if (!this._alvIlmoitusPoissa() && autosaved.alvValittu !== undefined && autosaved.alvValittu !== null) {
      asetaArvo(this.lomake.alvIlmoitus, autosaved.alvValittu)
    }
    if (!this._selvitettavatOstotPoissa() && autosaved.selvOstotValittu !== undefined && autosaved.selvOstotValittu !== null) {
      asetaArvo(this.lomake.selvitettavatOstot, autosaved.selvOstotValittu)
    }
    if (!this._selvitettavatMyynnitPoissa() && autosaved.selvMyynnitValittu !== undefined && autosaved.selvMyynnitValittu !== null) {
      asetaArvo(this.lomake.selvitettavatMyynnit, autosaved.selvMyynnitValittu)
    }
  }
}
